import {
    configureStore
} from '@reduxjs/toolkit';
import {
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
    StateReconciler,
    persistStore,
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import localStorage from "redux-persist/es/storage";

import rootReducer from "./reducers";
import {persistFilter as locationPersistFilter} from './features/location/slice';
import {persistFilter as mapPersistFilter} from './features/map/slice';
import {persistFilter as firestorePersistFilter} from "./features/firestore/slice";
import {persistFilter as cellsPersistFilter} from "./features/firestore/cells/slice";
import {persistFilter as authPersistFilter} from "./features/auth/slice";

const persistConfig = {
    key: 'root',
    version: 3,
    // v2 → v3: the default map style changed from Dark to Paper (light-v11).
    // Persisted dark-v11 is almost always the old default rather than an
    // explicit choice, so migrate it once; a deliberate re-pick of Dark after
    // this migration sticks (version marker prevents re-running).
    migrate: (state: any) => {
        if (state?.map?.styleUrl === 'mapbox://styles/mapbox/dark-v11') {
            state = { ...state, map: { ...state.map, styleUrl: 'mapbox://styles/mapbox/light-v11' } };
        }
        return Promise.resolve(state);
    },
    storage: localStorage,
    stateReconciler: autoMergeLevel2 as StateReconciler<any>,
    // 'tiles' is excluded from root persist — it has its own IndexedDB-backed
    // persistReducer in reducers.ts (via localforage). Blacklisting here prevents
    // double-persistence and avoids localStorage quota issues.
    blacklist: ['tiles'],
    transforms: [
        locationPersistFilter,
        mapPersistFilter,
        firestorePersistFilter,
        cellsPersistFilter,
        authPersistFilter,
    ]
};
const persistedReducer = persistReducer(
    persistConfig,
    rootReducer
);

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                warnAfter: 1000,
            },
            immutableCheck: {
                warnAfter: 1000,
            },
        }),
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk = (dispatch: AppDispatch, getState: () => RootState) => void;
